// BARCINO - Tunnel de commande hors Barba : accordéon et sauvegarde Ajax.
// intl-tel-input (~75 Ko) est chargé à la demande (voir utils/barcino_phone) :
// pas d'import statique ici pour ne pas le bundler sur les pages sans champ
// téléphone — webpack réutilise le même chunk déjà en cache une fois chargé.

const barcinoCommande = {
    _root: null,
    _panels: [],
    _completed: new Set(),
    _onClick: null,
    _onSubmit: null,
    _onChange: null,
    _onKeydown: null,
    _onDocKeydown: null,
    _onFocusout: null,
    _onResize: null,
    _emailCheckSequence: 0,
    _relayLoaded: false,
    _stripe: null,
    _stripeElements: null,
    _stripeExpress: null,
    _stripeOrderId: '',
    _stripeLoading: false,

    init() {
        this._root = document.querySelector('.bc-checkout');
        if (!this._root) return;

        this._panels = Array.from(this._root.querySelectorAll('[data-checkout-panel]'));
        this._panels.forEach(panel => {
            if (panel.classList.contains('is-complete')) this._completed.add(panel.dataset.checkoutPanel);
        });

        // Arrivée sur une étape autre que la première (ex : rechargement après
        // validation "Détails", qui atterrit sur ?step=delivery) — scroll
        // immédiat vers la section active au lieu de laisser la page en haut.
        const initialStep = this._root.dataset.checkoutStep;
        if (initialStep && initialStep !== 'identity') {
            const initialPanel = this._root.querySelector(`[data-checkout-panel="${initialStep}"]`);
            if (initialPanel) {
                requestAnimationFrame(() => initialPanel.scrollIntoView({ behavior: 'auto', block: 'start' }));
            }
        }

        this._onClick = event => {
            const errClose = event.target.closest('[data-checkout-error-close]');
            if (errClose && this._root.contains(errClose)) {
                event.preventDefault();
                this._hideError();
                return;
            }

            const cartToggle = event.target.closest('[data-checkout-cart-toggle]');
            if (cartToggle && this._root.contains(cartToggle)) {
                event.preventDefault();
                this._toggleCart();
                return;
            }

            const cartBackdrop = event.target.closest('[data-checkout-cart-backdrop]');
            if (cartBackdrop && this._root.contains(cartBackdrop)) {
                this._closeCart();
                return;
            }

            const relaySearch = event.target.closest('[data-relay-search]');
            if (relaySearch && this._root.contains(relaySearch)) {
                event.preventDefault();
                this._searchRelayPoints();
                return;
            }

            const relayShowMore = event.target.closest('[data-relay-show-more]');
            if (relayShowMore && this._root.contains(relayShowMore)) {
                event.preventDefault();
                this._root.querySelector('[data-relay-points]')?.classList.add('is-open');
                relayShowMore.hidden = true;
                return;
            }

            const relayGeolocate = event.target.closest('[data-relay-geolocate]');
            if (relayGeolocate && this._root.contains(relayGeolocate)) {
                event.preventDefault();
                this._locateRelayPoints();
                return;
            }

            const couponRemove = event.target.closest('[data-checkout-coupon-remove]');
            if (couponRemove && this._root.contains(couponRemove)) {
                event.preventDefault();
                this._removeCoupon();
                return;
            }

            const couponSuggestion = event.target.closest('[data-checkout-coupon-suggestion]');
            if (couponSuggestion && this._root.contains(couponSuggestion)) {
                event.preventDefault();
                const form = this._root.querySelector('[data-checkout-coupon-form]');
                const input = form?.querySelector('input[name="coupon"]');
                if (input) input.value = couponSuggestion.dataset.checkoutCouponSuggestion;
                if (form) this._applyCoupon(form);
                return;
            }

            const relayRefresh = event.target.closest('[data-relay-refresh]');
            if (relayRefresh && this._root.contains(relayRefresh)) {
                event.preventDefault();
                const points = this._root.querySelector('[data-relay-points]');
                const searchPanel = this._root.querySelector('[data-relay-search-panel]');
                searchPanel?.classList.add('is-open');
                if (this._relayLoaded && points) {
                    points.classList.remove('is-selected-view');
                    this._root.querySelector('[data-relay-address]')?.focus();
                } else {
                    this._loadRelayPoints(true);
                    this._root.querySelector('[data-relay-address]')?.focus();
                }
                return;
            }

            const trigger = event.target.closest('[data-checkout-open]');
            if (!trigger || !this._root.contains(trigger)) return;
            event.preventDefault();

            const step = trigger.dataset.checkoutOpen;
            if (step === 'details') this._completed.add('identity');
            this._open(step);
        };

        this._onSubmit = event => {
            const paymentForm = event.target.closest('[data-stripe-payment-form]');
            if (paymentForm && this._root.contains(paymentForm)) {
                event.preventDefault();
                this._submitPayment(paymentForm);
                return;
            }

            const couponForm = event.target.closest('[data-checkout-coupon-form]');
            if (couponForm && this._root.contains(couponForm)) {
                event.preventDefault();
                this._applyCoupon(couponForm);
                return;
            }

            const form = event.target.closest('[data-checkout-form]');
            if (!form || !this._root.contains(form)) return;
            event.preventDefault();
            this._save(form);
        };

        this._onChange = async event => {
            if (event.target.name === 'country') {
                const phoneInput = this._root.querySelector('[data-phone-input]');
                if (!phoneInput) return;
                const { default: intlTelInput } = await import('intl-tel-input/intlTelInputWithUtils');
                const iti = intlTelInput.getInstance(phoneInput);
                iti?.setSelectedCountry(event.target.value.toLowerCase());
                return;
            }

            if (event.target.name === 'same_billing') {
                const billingFields = this._root.querySelector('[data-checkout-billing-fields]');
                if (billingFields) billingFields.hidden = event.target.checked;
                return;
            }

            if (event.target.name === 'delivery_method') {
                const relay = this._root.querySelector('[data-checkout-relay]');
                const isPickup = event.target.dataset.deliveryType === 'pickup';
                relay?.classList.toggle('is-visible', isPickup);
                const submitLabel = this._root.querySelector('[data-delivery-submit-label]');
                if (submitLabel) {
                    submitLabel.textContent = isPickup
                        ? this._root.dataset.i18nDeliveryConfirmRelay
                        : this._root.dataset.i18nDeliveryContinue;
                }
                if (isPickup) {
                    const selectedCode = this._root.querySelector('[data-relay-selected-code]');
                    if (selectedCode) selectedCode.value = '';
                    this._relayLoaded = false;
                    this._loadRelayPoints(true);
                }
                return;
            }

            if (event.target.name === 'relay_point_choice') {
                const selectedCode = this._root.querySelector('[data-relay-selected-code]');
                if (selectedCode) selectedCode.value = event.target.value;
            }
        };

        this._onKeydown = event => {
            if (event.key !== 'Enter' || !event.target.matches('[data-relay-address]')) return;
            event.preventDefault();
            this._searchRelayPoints();
        };

        this._onDocKeydown = event => {
            if (event.key !== 'Escape') return;
            if (!this._root.querySelector('[data-checkout-error]')?.hidden) {
                this._hideError();
                return;
            }
            if (this._root.classList.contains('is-cart-open')) this._closeCart();
        };

        this._onResize = () => {
            if (window.innerWidth > 768 && this._root.classList.contains('is-cart-open')) {
                this._closeCart();
            }
        };

        this._onFocusout = event => {
            if (!event.target.matches('#checkout-email')) return;
            this._checkGuestEmail(event.target);
        };

        this._root.addEventListener('click', this._onClick);
        this._root.addEventListener('submit', this._onSubmit);
        this._root.addEventListener('change', this._onChange);
        this._root.addEventListener('keydown', this._onKeydown);
        this._root.addEventListener('focusout', this._onFocusout);
        document.addEventListener('keydown', this._onDocKeydown);
        window.addEventListener('resize', this._onResize);

        const selectedMethod = this._root.querySelector('input[name="delivery_method"]:checked');
        if (selectedMethod?.dataset.deliveryType === 'pickup') this._loadRelayPoints();
        if (this._root.dataset.checkoutStep === 'payment') this._initPayment();
    },

    destroy() {
        if (!this._root) return;
        this._hideError();
        this._setProcessing(false);
        this._closeCart();
        this._root.removeEventListener('click', this._onClick);
        this._root.removeEventListener('submit', this._onSubmit);
        this._root.removeEventListener('change', this._onChange);
        this._root.removeEventListener('keydown', this._onKeydown);
        this._root.removeEventListener('focusout', this._onFocusout);
        document.removeEventListener('keydown', this._onDocKeydown);
        window.removeEventListener('resize', this._onResize);
        this._root = null;
        this._panels = [];
        this._completed.clear();
        this._relayLoaded = false;
        this._stripe = null;
        this._stripeElements = null;
        this._stripeExpress = null;
        this._stripeOrderId = '';
        this._stripeLoading = false;
        this._emailCheckSequence = 0;
    },

    _toggleCart() {
        if (this._root.classList.contains('is-cart-open')) this._closeCart();
        else this._openCart();
    },

    _openCart() {
        const recap = this._root.querySelector('[data-checkout-cart-toggle]');
        const label = this._root.querySelector('[data-checkout-cart-label]');
        if (recap) {
            this._root.style.setProperty('--bc-cart-recap-h', `${recap.offsetHeight}px`);
            recap.setAttribute('aria-expanded', 'true');
            if (label && recap.dataset.i18nCartHide) label.textContent = recap.dataset.i18nCartHide;
        }
        this._root.querySelector('[data-checkout-cart-backdrop]')?.removeAttribute('hidden');
        this._root.classList.add('is-cart-open');
        document.body.style.overflow = 'hidden';
    },

    _closeCart() {
        const recap = this._root.querySelector('[data-checkout-cart-toggle]');
        const label = this._root.querySelector('[data-checkout-cart-label]');
        if (recap) {
            recap.setAttribute('aria-expanded', 'false');
            if (label && recap.dataset.i18nCartShow) label.textContent = recap.dataset.i18nCartShow;
        }
        this._root.querySelector('[data-checkout-cart-backdrop]')?.setAttribute('hidden', '');
        this._root.classList.remove('is-cart-open');
        document.body.style.overflow = '';
    },

    async _checkGuestEmail(input) {
        const form = input.closest('[data-checkout-form="details"]');
        const feedback = form?.querySelector('[data-checkout-email-feedback]');
        const submit = form?.querySelector('button[type="submit"]');
        if (!form || !feedback) return;

        const sequence = ++this._emailCheckSequence;
        feedback.textContent = '';
        input.removeAttribute('aria-invalid');

        if (!input.value.trim() || !input.checkValidity()) {
            submit?.removeAttribute('disabled');
            return;
        }

        const data = new FormData(form);
        data.set('check_email_only', '1');

        try {
            const response = await fetch(this._root.dataset.checkoutDetailsEndpoint, {
                method: 'POST',
                headers: {'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json'},
                body: data,
            });
            const result = await response.json();
            this._updateCsrf(result);
            if (sequence !== this._emailCheckSequence) return;

            if (!response.ok || !result.success) {
                feedback.textContent = result.message || this._root.dataset.i18nError;
                input.setAttribute('aria-invalid', 'true');
                submit?.setAttribute('disabled', 'true');
                return;
            }

            submit?.removeAttribute('disabled');
        } catch (error) {
            if (sequence !== this._emailCheckSequence) return;
            feedback.textContent = error.message || this._root.dataset.i18nError;
            submit?.removeAttribute('disabled');
        }
    },

    _open(step) {
        this._panels.forEach(panel => {
            const name = panel.dataset.checkoutPanel;
            const isActive = name === step;
            const isComplete = this._completed.has(name) && !isActive;
            const summary = panel.querySelector('[data-checkout-summary]');
            const content = panel.querySelector('[data-checkout-content]');
            const disabled = panel.querySelector('[data-checkout-disabled]');

            panel.classList.toggle('is-active', isActive);
            panel.classList.toggle('is-complete', isComplete);
            panel.classList.toggle('is-disabled', !isActive && !isComplete);
            if (summary) summary.hidden = !isComplete;
            if (content) content.hidden = !isActive;
            if (disabled) disabled.hidden = isActive || isComplete;
        });

        this._root.dataset.checkoutStep = step;
        const url = new URL(window.location.href);
        url.searchParams.set('step', step);
        window.history.replaceState({}, '', url);
        if (step === 'payment') this._initPayment();

        // Scroll vers la section qui vient de s'ouvrir (nouvelle étape, ou
        // retour sur une étape déjà complétée via "Modifier").
        const activePanel = this._root.querySelector(`[data-checkout-panel="${step}"]`);
        if (activePanel) {
            activePanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    },

    async _initPayment() {
        if (this._stripeElements || this._stripeLoading) return;

        const form = this._root.querySelector('[data-stripe-payment-form]');
        const mount = this._root.querySelector('[data-stripe-payment-element]');
        const feedback = this._root.querySelector('[data-stripe-payment-feedback]');
        const submit = this._root.querySelector('[data-stripe-payment-submit]');
        if (!form || !mount || typeof window.Stripe !== 'function') return;

        this._stripeLoading = true;
        if (feedback) feedback.textContent = this._root.dataset.i18nPaymentLoading;

        try {
            const response = await fetch(this._root.dataset.checkoutPaymentPrepareEndpoint, {
                method: 'POST',
                headers: {'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json'},
                body: new FormData(form),
            });
            const result = await this._readJsonResponse(response);
            this._updateCsrf(result);
            if (!response.ok || !result.success) throw new Error(result.message || '');

            this._stripe = window.Stripe(this._root.dataset.stripePublicKey, {
                locale: document.documentElement.lang === 'en' ? 'en' : 'fr',
            });
            this._stripeElements = this._stripe.elements({
                clientSecret: result.client_secret,
                appearance: {theme: 'stripe'},
            });
            this._stripeElements.create('payment', {layout: 'tabs'}).mount(mount);

            // Express Checkout Element : boutons Apple Pay / Google Pay au-dessus
            // du formulaire carte, sur le même PaymentIntent. La confirmation
            // rejoint le même flux de finalisation que le paiement carte.
            const expressMount = this._root.querySelector('[data-stripe-express-checkout]');
            const divider = this._root.querySelector('[data-stripe-payment-divider]');
            if (expressMount) {
                const express = this._stripeElements.create('expressCheckout');
                express.on('ready', (event) => {
                    if (event.availablePaymentMethods) {
                        // Wallets dispo : on révèle le séparateur « ou payer par carte ».
                        if (divider) divider.hidden = false;
                    } else {
                        // Aucun wallet sur cet appareil : on masque la zone (vide)
                        // pour ne pas laisser d'espace au-dessus du formulaire carte.
                        expressMount.hidden = true;
                    }
                });
                express.on('confirm', async () => {
                    this._setProcessing(true);
                    const {error} = await this._stripe.confirmPayment({
                        elements: this._stripeElements,
                        confirmParams: {return_url: form.dataset.returnUrl},
                        redirect: 'if_required',
                    });
                    if (error) {
                        this._setProcessing(false);
                        const msg = error.message || this._root.dataset.i18nError;
                        if (feedback) feedback.textContent = msg;
                        this._showError(msg);
                        return;
                    }
                    await this._completePayment(form);
                });
                express.mount(expressMount);
                this._stripeExpress = express;
            }

            this._stripeOrderId = result.order_id;
            form.dataset.returnUrl = result.return_url;
            if (feedback) feedback.textContent = '';
            submit?.removeAttribute('disabled');

            const url = new URL(window.location.href);
            if (url.searchParams.has('payment_intent')) this._completePayment(form);
        } catch (error) {
            const msg = error.message || this._root.dataset.i18nError;
            if (feedback) feedback.textContent = msg;
            this._showError(msg);
        } finally {
            this._stripeLoading = false;
        }
    },

    async _submitPayment(form) {
        if (!this._stripe || !this._stripeElements) return;
        const feedback = form.querySelector('[data-stripe-payment-feedback]');
        const submit = form.querySelector('[data-stripe-payment-submit]');
        submit?.setAttribute('disabled', 'true');
        form.classList.add('is-loading');
        this._setProcessing(true);
        if (feedback) feedback.textContent = this._root.dataset.i18nPaymentProcessing;

        const {error} = await this._stripe.confirmPayment({
            elements: this._stripeElements,
            confirmParams: {return_url: form.dataset.returnUrl},
            redirect: 'if_required',
        });
        if (error) {
            this._setProcessing(false);
            const msg = error.message || this._root.dataset.i18nError;
            if (feedback) feedback.textContent = msg;
            this._showError(msg);
            submit?.removeAttribute('disabled');
            form.classList.remove('is-loading');
            return;
        }
        await this._completePayment(form);
    },

    async _completePayment(form) {
        const feedback = form.querySelector('[data-stripe-payment-feedback]');
        this._setProcessing(true);
        if (feedback) feedback.textContent = this._root.dataset.i18nPaymentProcessing;
        const data = new FormData(form);
        data.set('order_id', this._stripeOrderId || new URL(window.location.href).searchParams.get('order') || '');

        // Garde-fou : si le serveur ne répond pas sous 45 s, on rend la main
        // au client avec un message clair plutôt qu'un spinner infini.
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 45000);

        try {
            const response = await fetch(this._root.dataset.checkoutPaymentCompleteEndpoint, {
                method: 'POST',
                headers: {'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json'},
                body: data,
                signal: controller.signal,
            });
            const result = await this._readJsonResponse(response);
            this._updateCsrf(result);
            if (!response.ok || !result.success) throw new Error(result.message || '');

            if (result.confirmation_url) {
                // On laisse la popin affichée pendant la redirection.
                window.location.assign(result.confirmation_url);
                return;
            }

            throw new Error(this._root.dataset.i18nError);
        } catch (error) {
            this._setProcessing(false);
            const message = error.name === 'AbortError'
                ? (this._root.dataset.i18nPaymentTimeout || this._root.dataset.i18nError)
                : (error.message || this._root.dataset.i18nError);
            if (feedback) feedback.textContent = message;
            this._showError(message);
            form.querySelector('[data-stripe-payment-submit]')?.removeAttribute('disabled');
            form.classList.remove('is-loading');
        } finally {
            clearTimeout(timeout);
        }
    },

    _setProcessing(on) {
        const overlay = this._root?.querySelector('[data-payment-processing]');
        if (!overlay) return;
        overlay.hidden = !on;
        document.body.style.overflow = on ? 'hidden' : '';
    },

    // Popin d'erreur : plus visible qu'un message sous le bouton.
    _showError(message) {
        const modal = this._root?.querySelector('[data-checkout-error]');
        if (!modal) return;
        const slot = modal.querySelector('[data-checkout-error-message]');
        if (slot) slot.textContent = message || this._root.dataset.i18nError || '';
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        modal.querySelector('[data-checkout-error-close]')?.focus();
    },

    _hideError() {
        const modal = this._root?.querySelector('[data-checkout-error]');
        if (!modal || modal.hidden) return;
        modal.hidden = true;
        if (!this._root.querySelector('[data-payment-processing]:not([hidden])')) {
            document.body.style.overflow = '';
        }
    },

    /**
     * Lit une réponse fetch attendue en JSON. Si le serveur renvoie du HTML
     * (erreur PHP, page 500, redirection de session…), on log le corps brut en
     * console pour diagnostiquer et on remonte un message lisible.
     */
    async _readJsonResponse(response) {
        const text = await response.text();
        try {
            return JSON.parse(text);
        } catch (parseError) {
            console.error(
                `[barcinoCommande] réponse non-JSON du serveur (HTTP ${response.status}) :\n`
                + text.slice(0, 4000),
            );
            throw new Error(this._root.dataset.i18nErrorServer || this._root.dataset.i18nError);
        }
    },

    async _save(form) {
        const step = form.dataset.checkoutForm;
        const endpoint = step === 'details'
            ? this._root.dataset.checkoutDetailsEndpoint
            : this._root.dataset.checkoutDeliveryEndpoint;
        const button = form.querySelector('button[type="submit"]');
        const feedback = form.querySelector('[data-checkout-feedback]');

        // intl-tel-input : injecter le numéro E.164 avant la sérialisation
        const phoneInput = form.querySelector('[data-phone-input]');
        if (phoneInput) {
            try {
                const { default: intlTelInput } = await import('intl-tel-input/intlTelInputWithUtils');
                const iti = intlTelInput.getInstance(phoneInput);
                if (iti) {
                    phoneInput.value = iti.getNumber();
                }
            } catch (_) { /* intl-tel-input non chargé */ }
        }

        const data = new FormData(form);

        button?.setAttribute('disabled', 'true');
        form.classList.add('is-loading');
        if (feedback) feedback.textContent = '';

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json'},
                body: data,
            });
            const result = await response.json();
            this._updateCsrf(result);
            if (!response.ok || !result.success) {
                const err = new Error(result.message || '');
                err.isValidation = response.status === 422;
                throw err;
            }

            if (step === 'details') {
                // Rechargement complet : les modes de livraison affichés dépendent du pays
                // (seuils/tarifs par zone), recalculés côté serveur à l'étape suivante.
                const url = new URL(window.location.href);
                url.searchParams.set('step', 'delivery');
                window.location.assign(url.toString());
                return;
            } else {
                this._updateDeliverySummary(result.delivery || {});
                this._updateSummaryTotals(result.totals || {});
                this._completed.add('delivery');
                this._open('payment');
            }
        } catch (error) {
            const msg = error.message || this._root.dataset.i18nError;
            if (feedback) feedback.textContent = msg;
            if (!error.isValidation) this._showError(msg);
        } finally {
            button?.removeAttribute('disabled');
            form.classList.remove('is-loading');
        }
    },

    async _applyCoupon(form) {
        const input = form.querySelector('input[name="coupon"]');
        const feedback = this._root.querySelector('[data-checkout-coupon-feedback]');
        const button = form.querySelector('button[type="submit"]');
        const code = input?.value.trim() || '';

        if (feedback) feedback.textContent = '';
        if (!code) {
            if (feedback) feedback.textContent = this._root.dataset.i18nCouponError;
            return;
        }

        button?.setAttribute('disabled', 'true');

        try {
            const response = await fetch(this._root.dataset.checkoutCouponEndpoint, {
                method: 'POST',
                headers: {'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json'},
                body: new FormData(form),
            });
            const result = await response.json();
            this._updateCsrf(result);

            if (!response.ok || !result.success) {
                if (feedback) feedback.textContent = result.message || this._root.dataset.i18nCouponError;
                return;
            }

            this._showCouponApplied(code);
            this._updateSummaryTotals(result.totals);
        } catch (error) {
            if (feedback) feedback.textContent = error.message || this._root.dataset.i18nCouponError;
        } finally {
            button?.removeAttribute('disabled');
        }
    },

    async _removeCoupon() {
        const form = this._root.querySelector('[data-checkout-coupon-form]');
        if (!form) return;

        try {
            const response = await fetch(this._root.dataset.checkoutCouponRemoveEndpoint, {
                method: 'POST',
                headers: {'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json'},
                body: new FormData(form),
            });
            const result = await response.json();
            this._updateCsrf(result);

            this._showCouponForm();
            this._updateSummaryTotals(result.totals);
        } catch (error) {
            console.error('[barcinoCommande] remove coupon', error);
        }
    },

    _showCouponApplied(code) {
        const form = this._root.querySelector('[data-checkout-coupon-form]');
        const applied = document.getElementById('bc-checkout-coupon-applied');
        const label = applied?.querySelector('[data-coupon-applied-label]');
        const feedback = this._root.querySelector('[data-checkout-coupon-feedback]');

        if (form) form.hidden = true;
        if (applied) applied.hidden = false;
        if (label) label.textContent = (this._root.dataset.i18nCouponApplied || '').replace('%s', code);
        if (feedback) feedback.textContent = '';

        this._root.querySelectorAll('[data-checkout-coupon-suggestion]').forEach((btn) => {
            const isApplied = btn.dataset.checkoutCouponSuggestion === code;
            btn.classList.toggle('is-applied', isApplied);
            btn.disabled = isApplied;
        });
    },

    _showCouponForm() {
        const form = this._root.querySelector('[data-checkout-coupon-form]');
        const applied = document.getElementById('bc-checkout-coupon-applied');
        const input = form?.querySelector('input[name="coupon"]');

        if (form) form.hidden = false;
        if (applied) applied.hidden = true;
        if (input) input.value = '';

        this._root.querySelectorAll('[data-checkout-coupon-suggestion]').forEach((btn) => {
            btn.classList.remove('is-applied');
            btn.disabled = false;
        });
    },

    _updateSummaryTotals(totals) {
        if (!totals) return;

        const set = (sel, val) => {
            const el = this._root.querySelector(sel);
            if (el) el.textContent = val;
        };
        const format = value => `${new Intl.NumberFormat(document.documentElement.lang === 'en' ? 'en-GB' : 'fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value || 0)} €`;

        set('[data-total-subtotal]', format(totals.subtotal));
        set('[data-total-final]', format(totals.total));
        set(
            '[data-total-shipping]',
            totals.shipping_pending
                ? this._root.dataset.i18nShippingPending
                : (totals.shipping === 0 ? this._root.dataset.i18nShippingFree : format(totals.shipping))
        );

        const discountLine = this._root.querySelector('[data-total-discount-line]');
        if (discountLine) {
            const hasDiscount = (totals.discount || 0) > 0;
            discountLine.hidden = !hasDiscount;
            if (hasDiscount) set('[data-total-discount]', `- ${format(totals.discount)}`);
        }

        const giftLine = this._root.querySelector('[data-total-gift-line]');
        if (giftLine) {
            const hasGift = !!(totals.gift && totals.gift.description);
            giftLine.hidden = !hasGift;
            if (hasGift) set('[data-total-gift]', totals.gift.description);
        }
    },

    _updateDetailsSummary(details) {
        const values = {
            email: details.email,
            phone: details.phone,
            address: `${details.line1}\n${details.postal_code} ${details.city}`,
        };

        Object.entries(values).forEach(([key, value]) => {
            const element = this._root.querySelector(`[data-checkout-value="${key}"]`);
            if (element) element.textContent = value;
        });
    },

    _updateDeliverySummary(delivery) {
        const values = {
            title: delivery.title || '',
            name: delivery.name || '',
            address: delivery.address || '',
        };

        Object.entries(values).forEach(([key, value]) => {
            const element = this._root.querySelector(`[data-delivery-summary-${key}]`);
            if (element) element.textContent = value;
        });

        const distance = this._root.querySelector('[data-delivery-summary-distance]');
        if (distance) {
            const value = delivery.distance || '';
            const label = distance.querySelector('span');
            if (label) label.textContent = value;
            distance.hidden = value === '';
        }
    },

    _updateCsrf(result) {
        if (!result.csrf_name || !result.csrf_hash) return;
        this._root.querySelectorAll(`input[name="${result.csrf_name}"]`).forEach(input => {
            input.value = result.csrf_hash;
        });
    },

    async _loadRelayPoints(force = false, coordinates = null, address = '') {
        if (this._relayLoaded && !force) return;

        const endpoint = this._root.dataset.checkoutRelayPointsEndpoint;
        const status = this._root.querySelector('[data-relay-status]');
        const container = this._root.querySelector('[data-relay-points]');
        if (!endpoint || !container) return;

        const selectedMethod = this._root.querySelector('input[name="delivery_method"]:checked');
        if (!selectedMethod || selectedMethod.dataset.deliveryType !== 'pickup') return;
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('offer_code', selectedMethod.value);
        if (coordinates) {
            url.searchParams.set('latitude', coordinates.latitude);
            url.searchParams.set('longitude', coordinates.longitude);
        } else if (address) {
            url.searchParams.set('address', address);
        }

        if (status) status.textContent = this._root.dataset.i18nRelayLoading;
        container.classList.remove('is-open', 'is-selected-view');
        container.replaceChildren();
        const showMore = this._root.querySelector('[data-relay-show-more]');
        if (showMore) showMore.hidden = true;

        try {
            const response = await fetch(url, {
                headers: {'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json'},
            });
            const result = await response.json();
            this._updateCsrf(result);
            if (!response.ok || !result.success) throw new Error(result.message || '');

            this._relayLoaded = true;
            this._renderRelayPoints(result.points || [], container);
            if (status) status.textContent = result.points?.length ? '' : this._root.dataset.i18nRelayEmpty;
        } catch (error) {
            if (status) status.textContent = error.message || this._root.dataset.i18nError;
        }
    },

    _searchRelayPoints() {
        const input = this._root.querySelector('[data-relay-address]');
        const address = input?.value.trim() || '';
        if (!address) {
            input?.focus();
            return;
        }

        const selectedCode = this._root.querySelector('[data-relay-selected-code]');
        if (selectedCode) selectedCode.value = '';
        this._relayLoaded = false;
        this._loadRelayPoints(true, null, address);
    },

    _locateRelayPoints() {
        const status = this._root.querySelector('[data-relay-status]');
        if (!navigator.geolocation) {
            if (status) status.textContent = this._root.dataset.i18nGeolocationUnavailable;
            return;
        }

        if (status) status.textContent = this._root.dataset.i18nGeolocationLoading;

        navigator.geolocation.getCurrentPosition(
            position => {
                const selectedCode = this._root.querySelector('[data-relay-selected-code]');
                if (selectedCode) selectedCode.value = '';
                this._relayLoaded = false;
                this._loadRelayPoints(true, {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            },
            error => {
                if (!status) return;
                status.textContent = error.code === error.PERMISSION_DENIED
                    ? this._root.dataset.i18nGeolocationDenied
                    : this._root.dataset.i18nGeolocationUnavailable;
            },
            {enableHighAccuracy: false, timeout: 10000, maximumAge: 300000},
        );
    },

    _renderRelayPoints(points, container) {
        const selectedCode = this._root.querySelector('[data-relay-selected-code]');
        const iconTemplate = this._root.querySelector('[data-relay-map-icon]');
        let hasSelectedPoint = false;

        points.forEach(point => {
            const label = document.createElement('label');
            label.className = 'bc-checkout-relay-point';

            const input = document.createElement('input');
            input.type = 'radio';
            input.name = 'relay_point_choice';
            input.value = point.code;
            input.checked = selectedCode?.value === point.code;
            if (input.checked) hasSelectedPoint = true;

            const body = document.createElement('span');
            body.className = 'bc-checkout-relay-point__body';

            const name = document.createElement('strong');
            name.className = '__body-02 _medium';
            name.textContent = point.name;

            const address = document.createElement('span');
            address.className = '__body-03';
            address.textContent = [point.street, `${point.postal_code} ${point.city}`.trim()].filter(Boolean).join(', ');

            const distance = document.createElement('span');
            distance.className = 'bc-checkout-relay-point__distance __body-03 _bold';
            if (iconTemplate) distance.append(iconTemplate.content.cloneNode(true));
            const distanceText = document.createElement('span');
            distanceText.textContent = `${point.distance} ${this._root.dataset.i18nRelayDistance}`;
            distance.append(distanceText);

            body.append(name, address);
            label.append(input, body, distance);
            container.append(label);
        });

        container.classList.toggle('is-selected-view', hasSelectedPoint);
        const showMore = this._root.querySelector('[data-relay-show-more]');
        if (showMore) showMore.hidden = hasSelectedPoint || points.length <= 5;
    },
};

export default barcinoCommande;
